import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { isLang, translator, type Lang, type T } from '@bingo/shared';

/**
 * Interface language (English or French), chosen per person. Before login it comes from this device
 * (last choice, else the browser language); after login from the person's account.
 */
const KEY = 'bingo.lang';

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* storage blocked */
  }
  return navigator.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

/** Read by the API client (X-Lang header) and the date helpers, outside React. */
export let activeLang: Lang = initialLang();

interface LangState {
  lang: Lang;
  t: T;
  setLang: (l: Lang) => void;
}
const Ctx = createContext<LangState | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(activeLang);
  const setLang = useCallback((l: Lang) => {
    activeLang = l;
    try {
      localStorage.setItem(KEY, l);
    } catch {
      /* storage blocked */
    }
    setLangState(l);
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo(() => ({ lang, t: translator(lang), setLang }), [lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang(): LangState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useLang outside LangProvider');
  return v;
}

/** The translate function for the current language: t('Add vehicle'), t('{n} pax', { n }). */
export const useT = (): T => useLang().t;
